import PropTypes from "prop-types"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useNavigationType } from "react-router-dom"
import { goBack, replace } from "redux-first-history"

import { history } from "app-store-v2/index"
import BaseModal from "components/ui/modal"

import { useScrollTracker } from "./scroll-tracker"

export const closeModal = () => {
  const action = history.action === "PUSH" ? history.goBack : history.replace
  action(`${history.location.pathname}${history.location.search}`)
}
export const openModal = (hash, replace = false) => {
  const action = replace ? history.replace : history.push
  action(`${history.location.pathname}${history.location.search}${hash}`)
}
/**
  NOTE!!! Only use this modal if we're using the router hash to open/close the modal,
  Otherwise import Modal directly from storybook.
  @param {string} show - router hash used to open/close modal
*/

const Modal = ({ afterShow, children, setModalContent, show, ...props }) => {
  const [openModal, toggleModal] = useState(false)
  const routerAction = useNavigationType()
  const { pathname, hash: routerHash, search } = useLocation()
  const dispatch = useDispatch()
  const { scrollPos } = useScrollTracker()

  const hashMatched = useMemo(
    () =>
      Array.isArray(show) ? show.includes(routerHash) : routerHash === show,
    [routerHash, show]
  )

  const hideModal = useCallback(() => {
    const action = routerAction === "PUSH" ? goBack : replace
    dispatch(action(`${pathname}${search}`))
  }, [routerAction, dispatch])

  useEffect(() => {
    toggleModal(hashMatched)

    if (hashMatched) {
      setModalContent && setModalContent(routerHash)
      afterShow && afterShow()
    }
  }, [hashMatched, routerHash])

  useEffect(() => {
    if (routerAction !== "PUSH" && hashMatched) {
      hideModal()
    }
  }, [])

  useEffect(() => {
    const hashEl = routerHash
      ? document.getElementById(routerHash.replace(/#/g, ""))
      : null
    if (routerHash && !hashEl) {
      window.scrollTo(0, scrollPos)
    }
  }, [routerHash])

  return (
    <BaseModal onClose={() => hideModal()} show={openModal} {...props}>
      {children}
    </BaseModal>
  )
}

Modal.propTypes = {
  afterShow: PropTypes.func,
  children: PropTypes.node,
  setModalContent: PropTypes.func,
  show: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
}

export default Modal
